import { FullBrand } from "~src/types/brand";
import { ApiTypes, api } from "../common";

const DISCOVER_PATHS: ApiTypes.API = {
  search: {
    path: `api/filter-brands/`,
    method: ApiTypes.HTTPMethod.POST,
  },
};

type BrandDiscoverResponse = {
  count: number;
  next: string;
  previous: string | null;
  results: FullBrand[];
};

type ScoreRanking = {
  gte: number;
  lte: number;
};

export enum BrandDiscoverOrdering {
  BrandNameAsc = "brandname",
  BrandNameDsc = "-brandname",
  ScalingScoreRankingAsc = "scaling_score_ranking",
  ScalingScoreRankingDsc = "-scaling_score_ranking",
  RevenueScoreRankingAsc = "revenue_score_ranking",
  RevenueScoreRankingDsc = "-revenue_score_ranking",
  LatestFbLikesAsc = "fblikes",
  LatestFbLikesDsc = "-fblikes",
  LatestInstagramFollowersAsc = "igfollowers",
  LatestInstagramFollowersDsc = "-igfollowers",
  FacebookAdCountAsc = "fbads_count",
  FacebookAdCountDsc = "-fbads_count",
  ShopifyReviewsCountAsc = "latest_shopifyreviews_count",
  ShopifyReviewsCountDsc = "-latest_shopifyreviews_count",
  TrustPilotReviewsCountAsc = "latest_numberreviews",
  TrustPilotReviewsCountDsc = "-latest_numberreviews",
  LatestFbReviewsAsc = "latest_fbreviews",
  LatestFbReviewsDsc = "-latest_fbreviews",
  AmazonReviewsCountAsc = "latest_amazonreviews",
  AmazonReviewsCountDsc = "-latest_amazonreviews",
  LatestRedditGrowthAsc = "latest_redditgrowth",
  LatestRedditGrowthDsc = "-latest_redditgrowth",
  LatestRedditMentionsAsc = "latest_redditmentions",
  LatestRedditMentionsDsc = "-latest_redditmentions",
  EstimatedMonthlyTrafficAsc = "estimated_monthly_traffic",
  EstimatedMonthlyTrafficDsc = "-estimated_monthly_traffic",
  EstimatedMonthlyOrdersAsc = "estimated_monthly_orders",
  EstimatedMonthlyOrdersDsc = "-estimated_monthly_orders",
  EstimatedMonthlyRevenueAsc = "estimated_monthly_revenue",
  EstimatedMonthlyRevenueDsc = "-estimated_monthly_revenue",
  ProductDataCountAsc = "count",
  ProductDataCountDsc = "-count",
  ProductDataMinPriceAsc = "min_price",
  ProductDataMinPriceDsc = "-min_price",
  ProductDataAvgPriceAsc = "avg_price",
  ProductDataAvgPriceDsc = "-avg_price",
  ProductDataMaxPriceAsc = "max_price",
  ProductDataMaxPriceDsc = "-max_price",
  NewProductsLast30DaysAsc = "last_30_days",
  NewProductsLast30DaysDsc = "-last_30_days",
  LastMonthTrafficAsc = "last_month_traffic",
  LastMonthTrafficDsc = "-last_month_traffic",
  Last3MonthsTrafficAsc = "avg_last_3_months",
  Last3MonthsTrafficDsc = "-avg_last_3_months",
  Last6MonthsTrafficAsc = "avg_last_6_months",
  Last6MonthsTrafficDsc = "-avg_last_6_months",
}

export interface BrandDiscoverParams extends BrandDiscoverFilters {
  page: number;
  page_size: number;
  ordering?: string;
}

type FilterValue = {
  filter_value: {
    gte: number;
    lte: number;
  };
};

export interface BrandDiscoverFilters {
  search_text?: string;
  revenue_score_ranking?: {
    filter_value: ScoreRanking;
  };
  scaling_score_ranking?: {
    filter_value: ScoreRanking;
  };
  sub_categories?: number[];
  country_codes?: string[];
  // Social
  latest_reditmentions?: FilterValue;
  fblikes?: FilterValue;
  fblikes_diff?: FilterValue;
  igfollowers?: FilterValue;
  instafollowers_diff?: FilterValue;
  // Reviews
  latest_numberreviews?: FilterValue;
  trustpilotreviews_diff?: FilterValue;
  latest_fbreviews__range?: FilterValue;
  fbreviews_diff__range?: FilterValue;
  latest_shopifyreviews_count__range?: FilterValue;
  shopifyreview_count_diff__range?: FilterValue;
  // FB Ads
  fbads_count?: FilterValue;
  "diff_last_month.numberfbads"?: FilterValue;
  active_fbads_count?: FilterValue;
  is_fb_ad_active?: number; //1 or 0
  // Store Traffic
  estimated_monthly_traffic?: FilterValue;
  estimated_monthly_orders?: FilterValue;
  estimated_monthly_revenue?: FilterValue;
  // Products
  count?: FilterValue;
  min_price?: FilterValue;
  avg_price?: FilterValue;
  max_price?: FilterValue;
  last_30_days?: FilterValue;
  // Tab Options
  is_dropshipping?: boolean | string;
  report_published?: boolean | string;
  tracked_stores?: boolean;
  action?: "tracked_stores";
  fb_ad_active?: boolean;
  duration?: 30 | 60 | 90;
}

export const DefaultBrandDiscoverFilters: BrandDiscoverParams = {
  page: 1,
  page_size: 30,
  duration: 30,
  revenue_score_ranking: {
    filter_value: {
      gte: 0,
      lte: 100,
    },
  },
  scaling_score_ranking: {
    filter_value: {
      gte: 0,
      lte: 1000,
    },
  },
};

export const getBrandDiscoverResult = async (payload: BrandDiscoverParams) => {
  // Destructure to separate pagination from filters
  const { page, page_size, ...filters } = payload;
  const params = { page, page_size };
  
  if (filters.tracked_stores) {
    filters.action = "tracked_stores";
    delete filters.tracked_stores;
  }
  if (filters.is_dropshipping) {
    filters.is_dropshipping = "true";
  }
  const endpoint = DISCOVER_PATHS.search;
  const response = await api<BrandDiscoverResponse>({
    method: endpoint.method,
    url: endpoint.path,
    params: params,
    data: filters,
  });
  return response.data;
};
